import React, { useState, useEffect } from "react";
import { SideNav } from "./ui-pending";
import { sideButtons } from "./fileSideBar";
import "./style.scss";
import {
  isTeacherLeaderDisabled,
  isTeacherLeaderTabAllowed,
  TEACHER_LEADER_TEACHER_TABS,
  PROFILE_DATA_UPDATED_EVENT,
} from "../../../../utils/permissions";
import {
  VIEW_CHAT_TAB_TITLE,
  buildTeacherChatUrl,
  isTeacherViewChatEnabled,
} from "./teacherDashboardLinks";
import { openDashboardWithAuth } from "../../../../utils/dashboardHandoff";
import { useEmailAccessConfig } from "../../../../utils/emailAccessConfig";

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  credentialId: any;
  teacherId: any;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, teacherId }) => {
  const [IsOpen, setIsOpen] = useState(false);
  const [tlDisabled, setTlDisabled] = useState(isTeacherLeaderDisabled());
  useEmailAccessConfig();

  useEffect(() => {
    const refresh = () => setTlDisabled(isTeacherLeaderDisabled());
    refresh();
    window.addEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
  }, []);

  const OnHandelClick = () => {
    setIsOpen(!IsOpen);
  };

  const openViewChat = () => {
    if (!isTeacherViewChatEnabled()) return;
    // Restore jwtToken if cleared by a prior dashboard logout, then open chat.
    openDashboardWithAuth(buildTeacherChatUrl(teacherId));
    if (typeof window !== "undefined" && window.innerWidth < 1200) {
      setIsOpen(false);
    }
  };

  const handleTabClick = (tabTitle: string, disabled: boolean, isExternal?: boolean) => {
    if (disabled) return;
    if (isExternal && tabTitle === VIEW_CHAT_TAB_TITLE) {
      openViewChat();
      return;
    }
    setActiveTab(tabTitle);
    // Collapse the mobile tab drawer after choosing a section
    if (typeof window !== "undefined" && window.innerWidth < 1200) {
      setIsOpen(false);
    }
  };

  const items = sideButtons
    .filter((item) => {
      const isExternal = Boolean((item as { external?: boolean }).external);
      if (isExternal && item.title === VIEW_CHAT_TAB_TITLE) {
        return isTeacherViewChatEnabled();
      }
      return true;
    })
    .map((item) => {
      const isExternal = Boolean((item as { external?: boolean }).external);
      // View Chat is hidden for TL via isTeacherViewChatEnabled; other tabs use TL allowlist
      const disabled =
        !isExternal &&
        tlDisabled &&
        !isTeacherLeaderTabAllowed(item.title, TEACHER_LEADER_TEACHER_TABS);
      return {
        key: item.title,
        label: item.title,
        disabled,
        isExternal,
        title: disabled
          ? "Disabled for Teacher Leaders"
          : isExternal
            ? "Open teacher dashboard chat"
            : undefined,
      };
    });

  // External links (View Chat) never show as the active section.
  const activeItem = items.find(
    (item) => !item.isExternal && item.key.toLowerCase() === activeTab.toLowerCase()
  );

  return (
    <SideNav
      label="Teacher profile sections"
      items={items}
      activeKey={activeItem?.key}
      onSelect={(key) => {
        const item = items.find((i) => i.key === key);
        if (item) handleTabClick(item.key, item.disabled, item.isExternal);
      }}
      toggleLabel={`${IsOpen ? "Hide sections" : "Sections"} · ${activeTab}`}
      open={IsOpen}
      onToggle={OnHandelClick}
    />
  );
};

export default Sidebar;
